import React, { useState } from 'react'
import { Redirect } from 'react-router-dom'
import { withRouter } from 'react-router'
import { useAlert } from 'react-alert'
import { useLazyQuery } from '@apollo/client'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import Container from '@material-ui/core/Container'
import TextField from '@material-ui/core/TextField'
import CircularProgress from '@material-ui/core/CircularProgress'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import { useAuthorization } from '../../services/authorization'
import {
  LINKS,
  EMAIL_REGEX,
  INVALID_EMAIL_ADDRESS_TEXT,
  PASS_REGEX,
  PASSWORD_EQUALITY_ERROR_TEXT,
  PASSWORD_COMPLEXITY_ERROR_TEXT
} from '../../constants'
import { useStyles } from './styles'
import logo from '../../assets/ojilogo.png'
import { FORGOT_PASSWORD, RESET_PASSWORD } from '../../network/auth/queries'

const ForgotPasswordPage = ({ history }) => {
  const alert = useAlert()
  const classes = useStyles()
  const [getAuthToken] = useAuthorization()
  const [email, setEmail] = useState('')
  const [emailAdressError, setEmailAdressError] = useState('')
  const [wasEmailSent, setWasEmailSent] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [confirmationCode, setConfirmationCode] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [matchPasswordsError, setMatchPasswordsError] = useState('')

  const [sendEmailQuery, { loading: isSendingEmail }] = useLazyQuery(FORGOT_PASSWORD, {
    variables: {
      userName: email.trim()
    },
    fetchPolicy: 'no-cache',
    skip: !email,
    onError: error => {
      backToLoginPage()
      alert.show(error.message, {
        type: 'error'
      })
    },
    onCompleted: () => {
      setWasEmailSent(true)
    }
  })

  const [resetPassword, { loading: isResettingPassword }] = useLazyQuery(RESET_PASSWORD, {
    onError: error => {
      alert.show(error.message, {
        type: 'error'
      })
    },
    fetchPolicy: 'no-cache',
    onCompleted: () => {
      alert.show('Password successfully changed.', {
        type: 'success'
      })
      backToLoginPage()
    }
  })

  const handleEmailChange = e => {
    setEmail(e.target.value)
    setEmailAdressError('')
  }

  const handleNewPasswordChange = e => {
    setNewPassword(e.target.value)
    setNewPasswordError('')
  }

  const handleConfirmPasswordChange = e => {
    setConfirmPassword(e.target.value)
    setMatchPasswordsError('')
  }

  const handleConfirmationCodeChange = e => {
    setConfirmationCode(e.target.value)
  }

  const isEmailValid = () => {
    const isValid = EMAIL_REGEX.test(email.toLowerCase().trim())
    if (!isValid) {
      setEmailAdressError(INVALID_EMAIL_ADDRESS_TEXT)
    }
    return isValid
  }

  const handleEmailSubmit = e => {
    e.preventDefault()
    if (isEmailValid()) {
      sendEmailQuery()
    }
  }

  const handleResetPasswordSubmit = e => {
    e.preventDefault()
    if (newPassword && confirmPassword) {
      if (newPassword.match(PASS_REGEX)) {
        if (newPassword === confirmPassword) {
          resetPassword({
            variables: {
              confirmationCode: confirmationCode.trim(),
              userName: email.trim(),
              newPassword
            }
          })
        } else {
          setMatchPasswordsError(PASSWORD_EQUALITY_ERROR_TEXT)
        }
      } else {
        setNewPasswordError(PASSWORD_COMPLEXITY_ERROR_TEXT)
      }
    }
  }

  const backToLoginPage = () => {
    history.push(LINKS.LOGIN)
  }

  return getAuthToken() ? (
    <Redirect to={LINKS.DASHBOARD} />
  ) : (
    <div className={classes.root}>
      <Grid container direction='column' justifyContent='center' alignItems='center' spacing={10}>
        <Grid container item direction='row' justifyContent='flex-start' alignItems='center'>
          <Grid item xs={3}>
            <img src={logo} alt='logo' onClick={backToLoginPage} className={classes.logo} />
          </Grid>
        </Grid>
        <Grid container item direction='row' justifyContent='center' alignItems='center'>
          <Grid item xs={8}>
            <Paper className={classes.paper}>
              {wasEmailSent ? (
                <Container>
                  <Typography variant='h1' component='div' className={classes.formHeader}>
                    Reset Password
                  </Typography>
                  <Typography variant='body1' component='div' className={classes.setupHint}>
                    Please enter a new password. Your password should contain at least 8 characters, uppercase and lowercase letters.
                  </Typography>
                  <form onSubmit={handleResetPasswordSubmit} className={classes.form}>
                    <div className={classes.inputBlock}>
                      <div className={classes.confirmRow}>
                        <div className={classes.codeHint}>
                          Please enter the confirmation code we sent to your email to reset your password.
                        </div>
                        {confirmationCode ? (
                          <Typography variant='h6' component='label' className={classes.label} htmlFor='Confirmation Code'>
                            CONFIRMATION CODE
                          </Typography>
                        ) : (
                          <div className={classes.labelPlaceHolder} />
                        )}
                        <TextField
                          classes={{
                            root: classes.input
                          }}
                          fullWidth
                          id='Confirmation Code'
                          placeholder='Confirmation Code'
                          variant='outlined'
                          name='Confirmation Code'
                          value={confirmationCode}
                          onChange={handleConfirmationCodeChange}
                          inputProps={{
                            autoComplete: 'confirmation-code'
                          }}
                        />
                      </div>
                      <div className={classes.inputRow}>
                        {newPassword ? (
                          <Typography variant='h6' component='label' className={classes.label} htmlFor='New Password'>
                            PASSWORD
                          </Typography>
                        ) : (
                          <div className={classes.labelPlaceHolder} />
                        )}
                        <TextField
                          classes={{
                            root: classes.input
                          }}
                          fullWidth
                          id='New Password'
                          placeholder='New Password'
                          variant='outlined'
                          name='New Password'
                          value={newPassword}
                          onChange={handleNewPasswordChange}
                          inputProps={{
                            type: 'password',
                            autoComplete: 'new-password'
                          }}
                        />
                      </div>
                      {newPasswordError && (
                        <Typography variant='h6' component='span' className={classes.errorLabel}>
                          {newPasswordError}
                        </Typography>
                      )}
                      <div className={classes.inputRow}>
                        {confirmPassword ? (
                          <Typography variant='h6' component='label' className={classes.label} htmlFor='Confirm Password'>
                            CONFIRM PASSWORD
                          </Typography>
                        ) : (
                          <div className={classes.labelPlaceHolder} />
                        )}
                        <TextField
                          classes={{
                            root: classes.input
                          }}
                          fullWidth
                          id='Confirm Password'
                          placeholder='Confirm Password'
                          variant='outlined'
                          name='Confirm Password'
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                          inputProps={{
                            type: 'password',
                            autoComplete: 'confirm-password'
                          }}
                        />
                      </div>
                      {matchPasswordsError && (
                        <Typography variant='h6' component='span' className={classes.errorLabel}>
                          {matchPasswordsError}
                        </Typography>
                      )}
                    </div>
                    <div className={classes.buttonBlock}>
                      {isResettingPassword ? (
                        <div className={classes.loader}>
                          <CircularProgress color='inherit' />
                        </div>
                      ) : (
                        <Button
                          disabled={!confirmationCode || !newPassword || !confirmPassword}
                          className={classes.saveButton}
                          type='submit'
                        >
                          RESET PASSWORD
                        </Button>
                      )}
                    </div>
                  </form>
                </Container>
              ) : (
                <Container>
                  <Typography variant='h1' component='div' className={classes.formHeader}>
                    Forgot Your Password?
                  </Typography>
                  <Typography variant='body1' component='div' className={classes.setupHint}>
                    Enter the email address associated with your Dashboard account. After you send, you'll receive an email that will help
                    you reset your password.
                  </Typography>
                  <form onSubmit={handleEmailSubmit} className={classes.form}>
                    <div className={classes.inputBlock}>
                      <div className={classes.inputRow}>
                        {email ? (
                          <Typography variant='h6' component='label' className={classes.label} htmlFor='EMAIL ADDRESS'>
                            EMAIL ADDRESS
                          </Typography>
                        ) : (
                          <div className={classes.labelPlaceHolder} />
                        )}
                        <TextField
                          classes={{
                            root: classes.input
                          }}
                          fullWidth
                          id='EMAIL ADDRESS'
                          placeholder='Email Address'
                          variant='outlined'
                          name='EMAIL ADDRESS'
                          value={email}
                          onChange={handleEmailChange}
                          inputProps={{
                            autoComplete: 'email-address'
                          }}
                        />
                      </div>
                      {emailAdressError && (
                        <Typography variant='h6' component='span' className={classes.errorLabel}>
                          {emailAdressError}
                        </Typography>
                      )}
                    </div>
                    <div className={classes.buttonBlock}>
                      {isSendingEmail ? (
                        <div className={classes.loader}>
                          <CircularProgress color='inherit' />
                        </div>
                      ) : (
                        <Button disabled={Boolean(emailAdressError) || !email} className={classes.saveButton} type='submit'>
                          SEND EMAIL
                        </Button>
                      )}
                    </div>
                  </form>
                </Container>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export default withRouter(ForgotPasswordPage)
